/**
 * QR Screen — a known guest re-scanning the host's live QR.
 *
 * The guest marked the host's LIVE session with the room the two already share
 * (services/guestReturn → mark_guest_returned). The host never forms a new room for
 * them: it checks the room is its own, decides the meeting (the same rule as every
 * repeat connect — meetingDecision), files a re-meet if it is one, tells the guest's
 * phone the new meeting, and moves off its QR into the existing conversation.
 * (Before: the guest marked the old room's dead session and the host stayed on its QR.)
 */

import { isSansChatVariant } from '../../utils/exchangeVariant';
import { currentUtcOffsetMinutes } from '../../utils/dateHelpers';
import { isHostOfRoom, resetAnonConnected } from '../../services/activeSession';
import { sendMessage } from '../../services/chat/chatService';
import { buildMetadataUpdatedMessage } from '../../services/chat/messageBuilders';
import { logConnectionError, logMeetingRepeatConnect } from '../../services/analytics/eventLogger';
import { decideHostMeeting, meetingRecordFrom } from '../../services/meetings/meetingDecision';
import { applyRepeatConnect } from '../../services/meetings/repeatConnect';
import { MEETING_KIND, minutesSinceMeetingStart } from '../../services/meetings/meetingRule';
import { stageConnectSheets } from '../../services/exchange/connectSheets';
import { getConnectionMetadata } from '../../services/storage/userDataStorage';
import { setReconnectPin } from '../../services/people/reconnectPin';
import { isNewExperience } from '../../utils/newExperience';

/**
 * The host moves off its QR into a conversation that already exists — a known guest
 * re-scanning (here) or a live re-scan of a rejoined session (qrDetection). One ending,
 * so both land in the same place for every variant.
 */
export const enterExistingRoom = (
  roomId,
  { updateSessionChatRoomId, navigateTo, onClose, onFormed }
) => {
  updateSessionChatRoomId(roomId, true);
  if (onFormed) {
    // The new code page (E3b-1 step 10): a repeat lands on new People — the
    // page leaves itself, with this person on top
    onFormed(roomId, { nameKnown: true, repeat: true });
    return;
  }
  if (isSansChatVariant()) {
    navigateTo('profile-connections-list', {});
  } else {
    navigateTo('profile-connection-page');
  }
  if (onClose) onClose();
};

/**
 * @param {string} returningRoomId - the room the guest says they share with this host
 * @param {Object} config - handleAnonDetected's config (refs + context methods)
 * @returns {Promise<boolean>} true when handled (the host moved to the conversation)
 */
export const handleReturningGuest = async (returningRoomId, config) => {
  const {
    profileUserId,
    locationDataRef,
    locationPromiseRef,
    coordsPromiseRef,
    updateConnectionMetadata,
  } = config;

  // The mark is consumed either way, so it can't capture the next QR open
  resetAnonConnected(profileUserId);

  // The room check and the meeting decision are independent — one round trip, not two
  const nowIso = new Date().toISOString();
  const [isMine, verdict] = await Promise.all([
    isHostOfRoom(returningRoomId, profileUserId),
    decideHostMeeting({
      existingChatRoomId: returningRoomId,
      profileUserId,
      coordsPromise: coordsPromiseRef?.current || null,
      nowIso,
      nowUtcOffset: currentUtcOffsetMinutes(),
    }),
  ]);
  if (!isMine) {
    logConnectionError('returning_guest_room_refused', { chatRoomId: returningRoomId });
    return false;
  }
  console.log(`[QR_SCREEN] Returning guest: meeting verdict ${verdict.kind}`);

  if (verdict.kind === MEETING_KIND.REMEET) {
    await applyRepeatConnect({
      kind: verdict.kind,
      chatRoomId: returningRoomId,
      profileUserId,
      freshProfileData: null, // the guest's card is unchanged by a re-scan
      meetingMetadata: meetingRecordFrom(verdict, {
        exchangeType: 'anon_profile',
        locationData: locationDataRef.current,
        nowIso,
      }),
      updateAnonData: null,
      updateConnectionMetadata,
      locationDataRef,
      locationPromiseRef,
      // The guest's place comes from the host's record (their phone gives none): the new
      // meeting, and its late place, travel to them — not awaited, the host moves on
      onRecord: (record) => {
        sendMessage(
          returningRoomId,
          buildMetadataUpdatedMessage({
            connectionMetadata: record,
            senderProfileUserId: profileUserId,
            ...(isNewExperience() ? { meeting: { kind: verdict.kind, key: verdict.key } } : {}),
          })
        );
      },
    });
  } else if (verdict.kind === MEETING_KIND.RECONNECT && isNewExperience()) {
    // A reconnect records nothing new, so until now the guest heard nothing.
    // Both phones land on People with the other on top (SYSTEM §4c-iv): the
    // host pins from its own verdict, and tells the guest — its FULL current
    // record (the guest merges a record whole) plus the verdict as its own field.
    setReconnectPin(returningRoomId);
    getConnectionMetadata(returningRoomId).then((record) => {
      if (!record) return; // never a partial record
      sendMessage(
        returningRoomId,
        buildMetadataUpdatedMessage({
          connectionMetadata: record,
          senderProfileUserId: profileUserId,
          meeting: { kind: verdict.kind, key: verdict.key },
        })
      );
    });
  }

  logMeetingRepeatConnect(returningRoomId, {
    kind: verdict.kind,
    minutesSinceMeetingStart: minutesSinceMeetingStart(verdict.existing, nowIso),
    exchangeType: 'anon_profile',
  });
  stageConnectSheets(returningRoomId, verdict.kind, { repeat: true });
  enterExistingRoom(returningRoomId, config);
  return true;
};
